<template>
    <div class="container-fluid">
        <div v-if="form.WorkOrderId" class="alert alert-info d-flex align-items-center mb-3 py-2">
            <i class="fas fa-lock me-2"></i>
            <span>This item is linked to <a href="javascript:void(0);" @click="$router.push('/invoices/edit/' + form.WorkOrderId)"><strong>Invoice {{ form.WorkOrder?.InvoiceNumber || '#' + form.WorkOrderId }}</strong></a> and cannot be edited.</span>
        </div>
        <fieldset :disabled="!!form.WorkOrderId">
        <!-- Row 1: Client & Request -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Client</span>
                    <client-ac 
                        v-model="form.CustomerNumber" 
                        inputclass="form-control form-control-sm"
                        placeholder="Search for a client..."
                    ></client-ac>
                </label>
            </div>
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Request/Job</span>
                    <job-ac 
                        v-model="form.RequestId" 
                        :client="form.CustomerNumber"
                        :pm-only="pmOnly"
                        placeholder="Search for a request..."
                    ></job-ac>
                </label>
            </div>
        </div>

        <!-- Row 2: Technician & Date -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Assigned To</span>
                    <tech-ac 
                        v-model="form.TechnicianId" 
                        placeholder="Search for a technician..."
                    ></tech-ac>
                </label>
            </div>
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Date</span>
                    <input
                        type="date" 
                        class="form-control form-control-sm"
                        v-model="form.DateAndTime"
                    />
                </label>
            </div>
        </div>

        <!-- Row 3: Miles & Vehicle -->
        <div class="row">
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">Miles</span>
                    <input
                        type="number" 
                        step="0.1"
                        class="form-control form-control-sm"
                        v-model="form.Miles"
                        placeholder="0.0"
                    />
                </label>
            </div>
            <div class="col-md-4 mb-3">
                <label><strong>Vehicle</strong></label>
                <div class="input-group">
                    <select 
                        v-if="vehicleOptions.length > 0"
                        class="form-select form-select-sm"
                        v-model="form.Vehicle"
                    >
                        <option :value="undefined" disabled selected>Select Vehicle...</option>
                        <option 
                            v-for="vehicle in vehicleOptions" 
                            :key="vehicle.value" 
                            :value="vehicle.value"
                        >
                            {{ vehicle.label }}
                        </option>
                    </select>
                    <select 
                        v-else
                        class="form-select form-select-sm"
                        disabled
                    >
                        <option>Use button to add vehicle</option>
                    </select>
                    <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="openVehicleSelector"
                        title="Manage vehicles"
                    >
                        <Search :size="16" />
                    </button>
                </div>
            </div>
            <div class="col-md-4 mb-3">
                <kt-select 
                    label="Status"
                    :options="status"
                    placeholder="Select Status"
                    v-model="form.Status"
                    label-key="label"
                    value-key="value"
                    inputclass="form-control form-control-sm"
                ></kt-select>
            </div>
        </div>

        <!-- Row 4: Odometer Start & End -->
        <div class="row">
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">Odometer Start</span>
                    <input
                        type="number" 
                        class="form-control form-control-sm"
                        v-model="form.OdometerStart"
                        placeholder="Starting odometer reading"
                    />
                </label>
            </div>
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">Odometer End</span>
                    <input
                        type="number" 
                        class="form-control form-control-sm"
                        v-model="form.OdometerEnd"
                        placeholder="Ending odometer reading"
                    />
                </label>
            </div>
            <div class="col-md-4 mb-3">
                <!-- Reimbursement Date -->
                <label class="d-block">
                    <span class="form-label">Reimbursement Date</span>
                    <input
                        type="date" 
                        class="form-control form-control-sm"
                        v-model="form.ReimbursementDate"
                    />
                </label>
            </div>
        </div>
      

        <!-- Row 6: Notes (full width) -->
        <div class="row">
            <div class="col-12 mb-3">
                <label class="d-block">
                    <span class="form-label">Notes</span>
                    <textarea
                        class="form-control form-control-sm"
                        rows="4"
                        v-model="form.Notes"
                        placeholder="Mileage notes..."
                    ></textarea>
                </label>
            </div>
        </div>
        </fieldset>
    </div>

    <!-- Vehicle Selector Modal -->
    <vehicle-selector-modal
        v-if="showVehicleSelector"
        :visible="showVehicleSelector"
        :current-value="form.Vehicle"
        :can-manage="isAdmin"
        @close="showVehicleSelector = false"
        @select="onVehicleSelected"
        @vehicles-changed="loadVehicles"
    />
</template>

<script lang="ts" setup>

import { ref, onMounted } from "vue";
import KtSelect from "@/components/UI/KtSelect.vue";
import ClientAc from "@/components/Clients/ClientAc.vue";
import JobAc from "@/components/Jobs/JobAc.vue";
import TechAc from "@/components/Techs/TechAc.vue";
import VehicleSelectorModal from "@/components/Mileage/VehicleSelectorModal.vue";
import OptionsService from "@/services/OptionsService";
import { usePermissions } from "@/composables/usePermissions";
import { Search } from 'lucide-vue-next';

import { MileageModel } from "@/models/MileageModel";

const props = defineProps({
    pmOnly: {
        type: Boolean,
        default: false
    }
});

const form = defineModel({
    type: MileageModel,
    default: new MileageModel()
});

const { isAdmin } = usePermissions();

// Vehicle options from Options table
const vehicleOptions = ref<{ label: string; value: string }[]>([]);
const showVehicleSelector = ref(false);
const VEHICLE_OPTION_KEY = 'vehicles_json';

interface Vehicle {
    name: string;
}

const loadVehicles = async () => {
    try {
        const optionValue = await OptionsService.get(VEHICLE_OPTION_KEY);
        if (optionValue) {
            const vehicles: Vehicle[] = JSON.parse(optionValue);
            vehicleOptions.value = vehicles.map(v => ({
                label: v.name,
                value: v.name
            }));
        } else {
            vehicleOptions.value = [];
        }
    } catch (error) {
        console.error('Error loading vehicles:', error);
        vehicleOptions.value = [];
    }
};

const openVehicleSelector = () => {
    showVehicleSelector.value = true;
};

const onVehicleSelected = (vehicle: Vehicle) => {
    form.value.Vehicle = vehicle.name;
    showVehicleSelector.value = false;
};

// MileageStatus - no store exists yet, using static list
const status = ref([
    { value: 0, label: "Not Started" },
    { value: 1, label: "In Progress" },
    { value: 2, label: "Billable" },
    { value: 3, label: "Not Billable" },
    { value: 4, label: "Billed" },
    { value: 5, label: "Not Billed" },
    { value: 6, label: "Completed" },
    { value: 7, label: "Deferred" },
    { value: 8, label: "Waiting" },
    { value: 9, label: "Project Work" },
    { value: 10, label: "Management Review" },
    { value: 11, label: "Lost Revenue" },
    { value: 12, label: "Comp/Discount" },
    { value: 13, label: "New Hire Training" },
    { value: 14, label: "Estimate Required" }
]);

onMounted(() => {
    loadVehicles();
});

</script>